import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {KeyboardEventHandler} from 'react';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {AddLessonStore} from './parts/AddLessonStore';
import {DeleteLessonStore} from './parts/DeleteLessonStore';
import {EditLessonStore} from './parts/EditLessonStore';
import {MoveLessonStore} from './parts/MoveLessonStore';
import {ReorderLessonsStore} from './parts/ReorderLessonsStore';
import {X4UnitLoader} from './X4UnitLoader';

type Mode = 'View' | 'Add' | 'Edit' | 'Delete' | 'ReOrder' | 'Move';

export class X4UnitStore {
  readonly loader: X4UnitLoader;

  @observable
  mode: Mode;

  @observable.ref
  addLesson: AddLessonStore | null;
  @observable.ref
  editLesson: EditLessonStore | null;
  @observable.ref
  deleteLesson: DeleteLessonStore | null;
  @observable.ref
  reorder: ReorderLessonsStore | null;
  @observable.ref
  move: MoveLessonStore | null;

  private readonly disposers = new Disposers();

  constructor(readonly unitId: number) {
    makeObservable(this);
    this.mode = 'View';

    this.loader = new X4UnitLoader(unitId);
    this.disposers.add(this.loader);
    this.addLesson = this.editLesson = this.deleteLesson =
      this.move = this.reorder = null;

    this.setup();
  }

  @action
  private clear = () => {
    this.mode = 'View';
    this.addLesson = this.editLesson = this.deleteLesson =
      this.move = this.reorder = null;
  };

  @computed
  get isSaving(): boolean {
    return (this.addLesson?.save?.isPending === true ||
      this.editLesson?.save?.isPending === true ||
      this.reorder?.save?.isPending === true ||
      this.deleteLesson?.save?.isPending === true ||
      this.move?.save?.isPending === true
    );
  }

  @action
  beginAdd = () => {
    this.mode = 'Add';
    this.addLesson = new AddLessonStore(this.unitId);
  };

  @action
  completeAdd = () => {
    this.addLesson?.complete();
  };

  @action
  beginEdit = (lessonId: number) => {
    this.mode = 'Edit';
    let lesson = this.loader.lessonById[lessonId]!;
    this.editLesson = new EditLessonStore(lesson);
  };

  @action
  completeEdit = () => {
    this.editLesson?.complete();
  };

  @action
  completeOnEnter: KeyboardEventHandler = (event) => {
    if (event.key !== 'Enter')
      return;
    if (this.mode === 'Add')
      this.completeAdd();
    if (this.mode === 'Edit')
      this.completeEdit();
  };

  @action
  beginMove = (lessonId: number) => {
    this.mode = 'Move';
    const lesson = this.loader.lessonById[lessonId];
    this.move = new MoveLessonStore(lesson, this.loader.lookup!);
  };

  @action
  completeMove = () => {
    this.move?.complete();
  };

  @action
  beginDelete = (lessonId: number) => {
    this.mode = 'Delete';
    this.deleteLesson = new DeleteLessonStore(lessonId);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deleteLesson?.complete();
  };

  @action
  beginReorder = () => {
    this.mode = 'ReOrder';
    const lessonIds = this.loader.lessons.map(x => x.Id);
    this.reorder = new ReorderLessonsStore(this.unitId, lessonIds);
  };

  @action
  reorderUp = (index: number) => {
    this.reorderSwap(index, index - 1);
  };

  @action
  reorderDown = (index: number) => {
    this.reorderSwap(index, index + 1);
  };

  @action
  private reorderSwap(index1: number, index2: number) {
    this.reorder?.swap(index1, index2);
  }

  @action
  completeReorder = () => {
    this.reorder?.complete();
  };

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.clear();
  };

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.addLesson?.save?.value,
        this.editLesson?.save?.value,
        this.deleteLesson?.save?.value,
        this.reorder?.save?.value,
        this.move?.save?.value
      ],
      this.reloadOnSuccessResponse
    ));
  }

  @action
  private reloadOnSuccessResponse = () => {
    for (let state of [
      this.addLesson?.save,
      this.editLesson?.save,
      this.deleteLesson?.save,
      this.reorder?.save,
      this.move?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.loader.refresh();
        this.clear();
      }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
